/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { dropTask, timeout } from 'ember-concurrency';
import _isNil from 'lodash/isNil';
import moment from 'moment-timezone';
import { inFilter, niFilter } from 'volta/utils/filters-utils';
import { formatISODate, formatLocalDate, formatRelativeDate } from 'volta/utils/date-utils';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enterMetaKeyLabel } from 'volta/utils/dom-utils';

import type User from 'volta/models/user';
import type UserProfile from 'volta/models/user-profile';
import type { TTaskResolveIf, TTaskType, UTask } from 'volta/models/types/tasks';
import type StoreService from 'volta/services/store';
import type AuthorizationService from 'volta/services/authorization';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type SessionUserService from 'volta/services/session-user';
import type { IResourceName } from 'volta/models/base-model';
import type { IntlService } from 'ember-intl';

interface BvTaskSignature {
  Args: {
    task: UTask;
    resourceName?: IResourceName;
    isSelected?: boolean;
    disabled?: boolean;
    isResolvable?: boolean;
    isDue?: boolean;
    isAssignable?: boolean;
    taskType?: TTaskType;
    authorizedUsers?: string[];
    isAutoResolvable?: boolean;
    /* Actions */
    onSelect?: (task?: UTask, forceDelete?: boolean) => void;
    onSubmit?: (task: UTask) => Promise<void>;
    onResolve?: (task: UTask) => Promise<void>;
    onDelete?: (task: UTask) => Promise<void>;
  };
  Blocks: {
    actions: [];
    payload: [unknown, unknown];
  };
  Element: HTMLDivElement;
}

const ResolveIfIcons = {
  MANUAL: 'checkbox-outline',
  OUT_OF_BUFFER_ALERT: 'timer-outline'
};

export default class BvTaskComponent extends Component<BvTaskSignature> {
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;
  @service store!: StoreService;
  @service authorization!: AuthorizationService;

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked assignableUsers: (User | UserProfile)[] = this.args.task.assignees;

  ResolveIfIcons = ResolveIfIcons;
  enterMetaKeyLabel = enterMetaKeyLabel;

  get resolveIf() {
    return this.args.task.resolveIf ?? 'MANUAL';
  }

  get readOnly() {
    return (
      this.args.disabled === true ||
      (!this.showResolvePopover && !this.canDelete) ||
      _isNil(this.args.resourceName)
    );
  }

  get canUpdate() {
    return this.canCmd('UpdateTask');
  }

  get canDelete() {
    return this.canCmd('DeleteTask');
  }

  get canResolve() {
    return this.canCmd('ResolveTask');
  }

  get canReopen() {
    return this.canCmd('ReopenTask');
  }

  get badgeColor() {
    const { taskType } = this.args.task;
    switch (taskType) {
      case 'EXECUTION_TASK': {
        return 'green';
      }
      case 'PLANNING_TASK': {
        return 'blue';
      }
      default: {
        return 'primary';
      }
    }
  }

  get isOwn() {
    return this.createdBy?.id === this.sessionUser.userId;
  }

  get createdBy() {
    return this.args.task.createdBy ?? this.sessionUser.user;
  }

  get createdAtDisplay() {
    const { createdAt } = this.args.task;
    return this.displayTimeAt(createdAt);
  }

  get resolvedAtDisplay() {
    const { resolvedAt } = this.args.task;
    return this.displayTimeAt(resolvedAt);
  }

  get formattedDueDate(): string | undefined {
    const dueDate = this.args.task.dueDateAsDate;
    return formatRelativeDate(dueDate, this.intl.t('today'), this.intl.t('tomorrow'));
  }

  get showResolvePopover() {
    return (
      this.args.isAutoResolvable === true && _isNil(this.args.task?.resolvedAt) && this.canUpdate
    );
  }

  handleBodyEnter = dropTask(async (_value: string, _id: string, event: KeyboardEvent) => {
    event.preventDefault();
    return this.args.onSubmit?.(this.args.task);
  });

  handleSubmitBtnClick = dropTask(async (event: MouseEvent) => {
    event.preventDefault();
    return await this.args.onSubmit?.(this.args.task);
  });

  handleResolve = dropTask(async (checked: boolean, e: InputEvent) => {
    e.stopPropagation();
    await timeout(checked ? 1000 : 500);
    this.args.task.resolvedAt = checked ? new Date() : null;
    return await this.args.onResolve?.(this.args.task);
  });

  handleDelete = dropTask(async () => {
    return await this.args.onDelete?.(this.args.task);
  });

  @action
  decorateUserQuery(query: IJsonApiQuery) {
    const { filter = {} } = query;
    filter['userType'] = niFilter(['API', 'SUPPLIER']);

    if (this.args.authorizedUsers?.length) {
      filter['id'] = inFilter(this.args.authorizedUsers ?? []);
    }
    query.filter = filter;
    return query;
  }

  @action
  changeAssignees(assigneeIds: string[], assignees: User[]) {
    this.args.task.assigneeIds = assigneeIds;
    this.assignableUsers = assignees;
  }

  @action
  changeResolution(resolveIf: TTaskResolveIf | undefined) {
    this.args.task.resolveIf = resolveIf;
    return this.args.onSubmit?.(this.args.task);
  }

  @action
  changeDueDate(dueDate: Date | undefined) {
    this.args.task.dueDate = dueDate ? formatISODate(dueDate) : null;
  }

  @action
  handleEscape(_event: KeyboardEvent) {
    this.handleClickOutside(true);
  }

  @action
  handleClickOutside(forceDelete: boolean = false) {
    if (this.args.isSelected) {
      this.args.onSelect?.(undefined, forceDelete);
    }
  }

  private canCmd(cmd: string) {
    return this.args.resourceName
      ? this.authorization.canStrict(this.args.resourceName.plural, cmd)
      : false;
  }

  private displayTimeAt(time?: Date | null): string | undefined {
    if (!time) {
      return undefined;
    }
    const mTime = moment.utc(time);
    const isToday = mTime.isSame(moment.utc(), 'day');
    const isYesterday = mTime.isSame(moment.utc().subtract(1, 'day'));
    return isToday || isYesterday
      ? this.intl.t(isToday ? 'today' : 'yesterday')
      : formatLocalDate(mTime);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvTask: typeof BvTaskComponent;
    'bv-task': typeof BvTaskComponent;
  }
}
